import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Heading,
  Container,
  Table,
  TableRow,
  TableData,
  LoadingSpinner,
  LoadingContainer,
  Input,    // Assuming Input styled component exists
  Select,   // Assuming Select styled component exists
  FlexContainer, // For layout of search and filter
  ModalOverlay,
  ModalContent,
  ListContainer,
  FieldContainer
} from '../../styled/common';
import TechnicianWorkingStockDetailTable from './TechnicianWorkingStockDetailTable';
import TechnicianFaultyStockDetailTable from './TechnicianFaultyStockDetailTable';
import TechnicianOptusReturnedStockDetailTable from './TechnicianOptusReturnedStockDetailTable';

const TechnicianAggregatedInventory = ({ user }) => {
  const [inventoryData, setInventoryData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedTech, setSelectedTech] = useState(null);
  const [stockType, setStockType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilterTech, setSelectedFilterTech] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [techOptions, setTechOptions] = useState([]); // State for storing unique technicians

  useEffect(() => {
    const fetchInventoryData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get('/api/inventory');
        const data = response.data;
        setInventoryData(data);
        setFilteredData(data);

        // Extract unique technicians dynamically from the data
        const uniqueTechnicians = [...new Set(data.map(item => item.tech_id._id))];
        setTechOptions(uniqueTechnicians.map(techId => {
          const tech = data.find(item => item.tech_id._id === techId).tech_id;
          return { id: tech._id, name: tech.name };
        }));
      } catch (error) {
        console.error('Error fetching inventory data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInventoryData();
  }, []);

  useEffect(() => {
    const filtered = inventoryData.filter(item => {
      const matchesMaterial = String(item.material_id.Material).toLowerCase().includes(searchTerm.toLowerCase());
      const matchesTech = selectedFilterTech ? item.tech_id._id === selectedFilterTech : true;
      return matchesMaterial && matchesTech;
    });

    setFilteredData(filtered);
  }, [searchTerm, selectedFilterTech, inventoryData]);

  const handleRowClick = (material, tech, type = null) => {
    setSelectedMaterial(material);
    setSelectedTech(tech);
    setStockType(type);
    setIsModalOpen(true);
  };

  const handleCloseDetail = () => {
    setSelectedMaterial(null);
    setSelectedTech(null);
    setStockType(null);
    setIsModalOpen(false);
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <ListContainer style={{ flex: '1 1 100%', maxWidth: '100%' }}>
        <Heading>Technician Aggregated Inventory</Heading>
        {/* Search and Filter Section */}
        <FlexContainer
          direction="column"
          justify="flex-start"
          align="left"
          gap="20px"
          style={{
            marginBottom: '20px',
            padding: '10px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
          }}
        >
          <FieldContainer>
            <label htmlFor="materialSearch">Search by Material:</label>
            <Input
              id="materialSearch"
              type="text"
              placeholder="Search by material"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </FieldContainer>

          <FieldContainer>
            <label htmlFor="techFilter">Filter by Technician:</label>
            <Select
              id="techFilter"
              value={selectedFilterTech}
              onChange={(e) => setSelectedFilterTech(e.target.value)}
            >
              <option value="">All Technicians</option>
              {techOptions.map(tech => (
                <option key={tech.id} value={tech.id}>
                  {tech.name}
                </option>
              ))}
            </Select>
          </FieldContainer>
        </FlexContainer>

        {/* Data Table */}
        <Table>
          <thead>
            <tr>
              <th>Technician</th>
              <th>Material</th>
              <th>Total Working Stock On Hand</th>
              <th>Total Faulty Stock On Hand</th>
              <th>Total Faulty Stock Returned to Optus</th>
            </tr>
          </thead>
          <tbody>
            {filteredData
              .filter((item) => item.totalWorkingStockOnHand !== 0 || item.totalFaultyStock !== 0 || item.totalOptusReturnedStock !== 0) // Exclude rows with both quantities as 0
              .map((item) => (
                <TableRow key={`${item.tech_id._id}-${item.material_id._id}`}>
                  <TableData data-label="Technician">{item.tech_id.name}</TableData>
                  <TableData data-label="Material">
                    {item.material_id.Material} - {item.material_id.MaterialDescription}
                  </TableData>
                  <TableData
                    data-label="Total Working Stock On Hand"
                    onClick={() => handleRowClick(item.material_id._id, item.tech_id._id, 'working')}
                  >
                    {item.totalWorkingStockOnHand}
                  </TableData>
                  <TableData
                    data-label="Total Faulty Stock On Hand"
                    onClick={() => handleRowClick(item.material_id._id, item.tech_id._id, 'faulty')}
                  >
                    {item.totalFaultyStock}
                  </TableData>
                  <TableData
                    data-label="Total Faulty Stock Returned to Optus"
                    onClick={() => handleRowClick(item.material_id._id, item.tech_id._id, 'optusReturned')}
                  >
                    {item.totalOptusReturnedStock}
                  </TableData>
                </TableRow>
            ))}
          </tbody>
        </Table>
      </ListContainer>

      {/* Modal implementation */}
      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            {selectedMaterial && selectedTech && (
              stockType === 'faulty' ? (
                <TechnicianFaultyStockDetailTable
                  user={user}
                  material={selectedMaterial}
                  tech={selectedTech}
                  onClose={handleCloseDetail}
                />
              ) : stockType === 'optusReturned' ? (
                <TechnicianOptusReturnedStockDetailTable
                  user={user}
                  material={selectedMaterial}
                  tech={selectedTech}
                  onClose={handleCloseDetail}
                />
              ) : (
                <TechnicianWorkingStockDetailTable
                  user={user}
                  material={selectedMaterial}
                  tech={selectedTech}
                  onClose={handleCloseDetail}
                />
              )
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default TechnicianAggregatedInventory;
